import { parseSafeToPath, resolveSafeToPath } from "./destination";

export const GOOGLE_OAUTH_CALLBACK_PATH = "/app/auth/oauth-callback/";
export const GOOGLE_OAUTH_PENDING_KEY = "teksta.google-oauth.pending";
export const GOOGLE_OAUTH_MAX_AGE_MS = 300_000;

export type GoogleOAuthEntryPoint = "sign-in" | "sign-up";
export type GoogleOAuthFailureReason =
  | "cancelled"
  | "provider_rejected"
  | "provider_state_expired"
  | "local_flow_invalid"
  | "exchange_failed"
  | "storage_unavailable";

export type PendingGoogleOAuth = {
  flowId: string;
  codeVerifier: string;
  destination: string;
  entryPoint: GoogleOAuthEntryPoint;
  createdAtMs: number;
};

export type CapturedAuthUrl =
  | {
      kind: "oauth-callback";
      code: string | null;
      error: string | null;
      providerError: string | null;
      state: string | null;
    }
  | { kind: "invalid-provider-state" }
  | { kind: "legacy-refresh"; refreshToken: string }
  | { kind: "email-link-error" };

type OAuthStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export type PendingGoogleOAuthResult =
  | { status: "valid"; pending: PendingGoogleOAuth }
  | { status: "missing" | "invalid" | "unavailable" };

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const codeVerifierPattern = /^[A-Za-z0-9_-]{43,128}$/;

const isEntryPoint = (value: unknown): value is GoogleOAuthEntryPoint =>
  value === "sign-in" || value === "sign-up";

export const isGoogleOAuthFailureReason = (value: unknown): value is GoogleOAuthFailureReason =>
  value === "cancelled" ||
  value === "provider_rejected" ||
  value === "provider_state_expired" ||
  value === "local_flow_invalid" ||
  value === "exchange_failed" ||
  value === "storage_unavailable";

const parsePendingGoogleOAuth = (value: unknown, nowMs: number): PendingGoogleOAuth | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const destination =
    typeof record.destination === "string" ? parseSafeToPath(record.destination) : null;
  const createdAtMs = typeof record.createdAtMs === "number" ? record.createdAtMs : Number.NaN;
  const ageMs = nowMs - createdAtMs;

  if (
    typeof record.flowId !== "string" ||
    !uuidPattern.test(record.flowId) ||
    typeof record.codeVerifier !== "string" ||
    !codeVerifierPattern.test(record.codeVerifier) ||
    !destination ||
    !isEntryPoint(record.entryPoint) ||
    !Number.isInteger(createdAtMs) ||
    !Number.isFinite(ageMs) ||
    ageMs < 0 ||
    ageMs > GOOGLE_OAUTH_MAX_AGE_MS
  ) {
    return null;
  }

  return {
    flowId: record.flowId,
    codeVerifier: record.codeVerifier,
    destination,
    entryPoint: record.entryPoint,
    createdAtMs,
  };
};

export const savePendingGoogleOAuth = (
  storage: OAuthStorage | null,
  pending: PendingGoogleOAuth
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(GOOGLE_OAUTH_PENDING_KEY, JSON.stringify(pending));
    return true;
  } catch {
    return false;
  }
};

export const readPendingGoogleOAuth = (
  storage: OAuthStorage | null,
  nowMs = Date.now()
): PendingGoogleOAuthResult => {
  if (!storage) return { status: "unavailable" };
  let raw: string | null;
  try {
    raw = storage.getItem(GOOGLE_OAUTH_PENDING_KEY);
  } catch {
    return { status: "unavailable" };
  }
  if (!raw) return { status: "missing" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  const pending = parsePendingGoogleOAuth(parsed, nowMs);
  if (pending) return { status: "valid", pending };

  try {
    storage.removeItem(GOOGLE_OAUTH_PENDING_KEY);
  } catch {
    return { status: "unavailable" };
  }
  return { status: "invalid" };
};

export const clearPendingGoogleOAuth = (storage: OAuthStorage | null): boolean => {
  if (!storage) return false;
  try {
    storage.removeItem(GOOGLE_OAUTH_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
};

export const getGoogleOAuthSessionStorage = (): OAuthStorage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const buildGoogleOAuthRetryHref = (
  entryPoint: GoogleOAuthEntryPoint,
  destination: string
) => {
  const path = entryPoint === "sign-up" ? "/app/auth/sign-up" : "/app/auth/sign-in";
  const params = new URLSearchParams({
    toPath: resolveSafeToPath({ defaultPath: "/app/dashboard/", toPathParam: destination }),
  });
  return `${path}?${params.toString()}`;
};

export const buildGoogleOAuthRecoveryPath = ({
  destination,
  entryPoint,
  reason,
}: {
  destination: string;
  entryPoint: GoogleOAuthEntryPoint;
  reason: GoogleOAuthFailureReason;
}) => {
  const params = new URLSearchParams({
    oauthFailure: reason,
    oauthEntryPoint: entryPoint,
    toPath: resolveSafeToPath({ defaultPath: "/app/dashboard/", toPathParam: destination }),
  });
  return `${GOOGLE_OAUTH_CALLBACK_PATH}?${params.toString()}`;
};

const providerFailureParameters = [
  "provider_state",
  "provider_error",
  "provider_error_description",
  "provider_error_url",
  "error",
  "errorDescription",
];

export const sanitizeAuthUrl = (value: string): string => {
  const isAbsolute = /^[A-Za-z][A-Za-z\d+.-]*:/.test(value);
  try {
    const url = new URL(value, "https://teksta.invalid");
    const hasRefreshToken = url.searchParams.has("refreshToken");
    const isOAuthCallback = url.pathname.includes(GOOGLE_OAUTH_CALLBACK_PATH);
    const isProviderFailure =
      url.searchParams.has("provider_state") || url.searchParams.get("error") === "invalid-state";

    if (hasRefreshToken) {
      url.searchParams.delete("refreshToken");
      url.searchParams.delete("type");
    }
    if (url.pathname.includes("/app/auth/email-verification")) {
      for (const parameter of ["ticket", "code", "type", ...providerFailureParameters])
        url.searchParams.delete(parameter);
    }
    if (isOAuthCallback) {
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      for (const parameter of providerFailureParameters) url.searchParams.delete(parameter);
    } else if (isProviderFailure) {
      for (const parameter of providerFailureParameters) url.searchParams.delete(parameter);
    }
    if (isAbsolute) return url.toString();
    return url.pathname + url.search + url.hash;
  } catch {
    return value;
  }
};

const capturedAuthUrlKey = Symbol.for("teksta.captured-auth-url");

const getAuthUrlMemory = () => window as unknown as { [key: symbol]: CapturedAuthUrl | undefined };

export const captureSensitiveAuthUrl = (): void => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  let captured: CapturedAuthUrl | null = null;
  if (url.pathname.includes(GOOGLE_OAUTH_CALLBACK_PATH)) {
    const hasRawCallback = ["code", "state", "provider_error", "error"].some((parameter) =>
      url.searchParams.has(parameter)
    );
    if (hasRawCallback) {
      captured = {
        kind: "oauth-callback",
        code: url.searchParams.get("code"),
        error: url.searchParams.get("error"),
        providerError: url.searchParams.get("provider_error"),
        state: url.searchParams.get("state"),
      };
    }
  } else {
    const refreshToken = url.searchParams.get("refreshToken");
    if (refreshToken) {
      captured = { kind: "legacy-refresh", refreshToken };
    } else if (
      url.pathname.includes("/app/auth/email-verification") &&
      url.searchParams.has("error")
    ) {
      captured = { kind: "email-link-error" };
    } else if (url.searchParams.get("error") === "invalid-state") {
      captured = { kind: "invalid-provider-state" };
    }
  }

  if (!captured) return;
  getAuthUrlMemory()[capturedAuthUrlKey] = captured;
  window.history.replaceState({}, "", sanitizeAuthUrl(url.href));
};

export const peekCapturedAuthUrl = (): CapturedAuthUrl | null => {
  if (typeof window === "undefined") return null;
  return getAuthUrlMemory()[capturedAuthUrlKey] ?? null;
};

export const consumeCapturedAuthUrl = (): CapturedAuthUrl | null => {
  const captured = peekCapturedAuthUrl();
  if (typeof window !== "undefined") delete getAuthUrlMemory()[capturedAuthUrlKey];
  return captured;
};

const analyticsUrlProperties = [
  "$current_url",
  "$referrer",
  "$session_entry_url",
  "$session_entry_referrer",
  "$initial_current_url",
  "$initial_referrer",
];

export const sanitizeAuthUrlProperties = (
  properties: Record<string, unknown> | undefined
): void => {
  if (!properties) return;
  for (const property of analyticsUrlProperties) {
    const value = properties[property];
    if (typeof value === "string") properties[property] = sanitizeAuthUrl(value);
  }
};

export const classifyProviderFailure = (
  error: string | null,
  providerError: string | null
): GoogleOAuthFailureReason => {
  if (error === "invalid-state") return "provider_state_expired";
  if (providerError === "access_denied") return "cancelled";
  return "provider_rejected";
};

export const toGoogleOAuthAnalyticsEntryPoint = (
  entryPoint: GoogleOAuthEntryPoint
): "sign_in" | "sign_up" => (entryPoint === "sign-up" ? "sign_up" : "sign_in");
